import React, { useState, useEffect } from 'react';

function Templates() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [filter, setFilter] = useState('all');
  const [msg, setMsg] = useState(null);
  const [sendModal, setSendModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [sendTo, setSendTo] = useState('');
  const [params, setParams] = useState([]);
  const [sending, setSending] = useState(false);

  // headerMediaId editing state
  const [editingMediaId, setEditingMediaId] = useState(null); // template id being edited
  const [mediaIdInput, setMediaIdInput] = useState('');
  const [savingMediaId, setSavingMediaId] = useState(false);

  const API = process.env.REACT_APP_WHATSAPP_API || 'https://whatsapp.socialpartners.in/api';

  useEffect(() => { fetchTemplates(); fetchContacts(); }, []);

  const showMsg = (text, type = 'success') => {
    setMsg({ text, type });
    setTimeout(() => setMsg(null), 3500);
  };

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API}/templates`);
      const data = await res.json();
      setTemplates(Array.isArray(data) ? data : data.data || []);
    } catch (err) {
      showMsg('Failed to load templates', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchContacts = async () => {
    try {
      const res = await fetch(`${API}/wa-contacts`);
      const data = await res.json();
      setContacts(data.data || []);
    } catch (err) {}
  };

  const syncTemplates = async () => {
    setSyncing(true);
    try {
      const res = await fetch(`${API}/templates/sync`);
      const data = await res.json();
      showMsg(`Synced ${data.count || 0} templates from Meta`);
      fetchTemplates();
    } catch (err) {
      showMsg('Sync failed', 'error');
    } finally {
      setSyncing(false);
    }
  };

  // Get header format for a template (IMAGE / VIDEO / DOCUMENT / null)
  const getHeaderFormat = (template) => {
    const header = (template.components || []).find(c => c.type === 'HEADER');
    if (!header) return null;
    return ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(header.format) ? header.format : null;
  };

  const saveMediaId = async (templateId) => {
    if (!mediaIdInput.trim()) { showMsg('Enter a media ID', 'error'); return; }
    setSavingMediaId(true);
    try {
      const res = await fetch(`${API}/templates/${templateId}/header-media`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ headerMediaId: mediaIdInput.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to save');
      showMsg('Media ID saved successfully');
      setEditingMediaId(null);
      setMediaIdInput('');
      fetchTemplates(); // refresh to show updated value
    } catch (err) {
      showMsg(err.message, 'error');
    } finally {
      setSavingMediaId(false);
    }
  };

  const openSend = (template) => {
    setSelectedTemplate(template);
    setSendTo('');
    const body = (template.components || []).find(c => c.type === 'BODY');
    const bodyText = body?.text || '';
    const paramCount = (bodyText.match(/\{\{(\d+)\}\}/g) || []).length;
    setParams(Array(paramCount).fill(''));
    setSendModal(true);
  };

  const sendTemplate = async () => {
    if (!sendTo) { showMsg('Select a recipient', 'error'); return; }
    setSending(true);
    try {
      const res = await fetch(`${API}/send-template`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to: sendTo,
          templateName: selectedTemplate.name,
          languageCode: selectedTemplate.language || 'en',
          parameters: params,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Send failed');
      showMsg('Template sent successfully!');
      setSendModal(false);
    } catch (err) {
      showMsg(err.message, 'error');
    } finally {
      setSending(false);
    }
  };

  const getBodyText = (template) => {
    const body = (template.components || []).find(c => c.type === 'BODY');
    return body?.text || 'No body content';
  };

  const getStatusStyle = (status) => {
    const s = (status || 'PENDING').toLowerCase();
    const map = {
      approved: { background: '#dcfce7', color: '#15803d' },
      pending: { background: '#fef9c3', color: '#a16207' },
      rejected: { background: '#fee2e2', color: '#dc2626' },
      paused: { background: '#f3f4f6', color: '#6b7280' },
    };
    return map[s] || map.pending;
  };

  const filtered = filter === 'all'
    ? templates
    : templates.filter(t => t.status === filter || t.category === filter);

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">

        {msg && (
          <div style={{
            position: 'fixed', top: 20, right: 20, zIndex: 9999,
            background: msg.type === 'error' ? '#ef4444' : '#22c55e',
            color: '#fff', padding: '10px 20px', borderRadius: 8,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)', fontSize: 14,
          }}>
            {msg.text}
          </div>
        )}

        {/* Header */}
        <div className="page-header">
          <div className="row align-items-center">
            <div className="col">
              <h3 className="page-title">Templates</h3>
              <ul className="breadcrumb">
                <li className="breadcrumb-item"><a href="/dashboard">Dashboard</a></li>
                <li className="breadcrumb-item active">Templates</li>
              </ul>
            </div>
            <div className="col-auto">
              <button className="btn btn-primary" onClick={syncTemplates} disabled={syncing}>
                <i className="fas fa-sync-alt mr-1"></i>
                {syncing ? 'Syncing...' : 'Sync from Meta'}
              </button>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="row mb-3">
          <div className="col">
            {['all', 'APPROVED', 'PENDING', 'REJECTED', 'MARKETING', 'UTILITY', 'AUTHENTICATION'].map(f => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                style={{
                  marginRight: 8, marginBottom: 8,
                  padding: '5px 16px', borderRadius: 20,
                  border: filter === f ? '1.5px solid #128C7E' : '1.5px solid #e0e0e0',
                  background: filter === f ? '#e8f5e9' : '#fff',
                  color: filter === f ? '#128C7E' : '#666',
                  cursor: 'pointer', fontSize: 13, fontWeight: 500,
                }}
              >
                {f === 'all' ? 'All' : f.charAt(0) + f.slice(1).toLowerCase()}
              </button>
            ))}
            <span style={{ color: '#999', fontSize: 13, marginLeft: 8 }}>{filtered.length} templates</span>
          </div>
        </div>

        {/* Template Cards */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: 60, color: '#999' }}>Loading templates...</div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 60, color: '#999' }}>
            No templates found. Click "Sync from Meta" to load your templates.
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 16 }}>
            {filtered.map(t => {
              const headerFormat = getHeaderFormat(t);
              const hasMediaId = !!t.headerMediaId;
              const isEditing = editingMediaId === t.id;

              return (
                <div key={t.id} className="card" style={{ borderRadius: 10, overflow: 'hidden' }}>
                  {/* Card Header */}
                  <div style={{
                    padding: '14px 16px', borderBottom: '1px solid #f0f0f0',
                    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
                  }}>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 13, fontFamily: 'monospace', marginBottom: 3 }}>
                        {t.name}
                      </div>
                      <div style={{ fontSize: 11, color: '#999', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        {t.language} · {t.category}
                      </div>
                    </div>
                    <span style={{
                      ...getStatusStyle(t.status),
                      padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600,
                    }}>
                      {t.status || 'PENDING'}
                    </span>
                  </div>

                  {/* Body preview */}
                  <div style={{ padding: '12px 16px', fontSize: 13, color: '#555', lineHeight: 1.6, borderBottom: '1px solid #f0f0f0', minHeight: 70 }}>
                    {getBodyText(t).slice(0, 150)}{getBodyText(t).length > 150 ? '…' : ''}
                  </div>

                  {/* Media ID section — only for IMAGE/VIDEO/DOCUMENT header templates */}
                  {headerFormat && (
                    <div style={{
                      padding: '10px 16px', borderBottom: '1px solid #f0f0f0',
                      background: hasMediaId ? '#f0fdf4' : '#fffbeb',
                    }}>
                      <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 6, color: hasMediaId ? '#15803d' : '#92400e' }}>
                        {headerFormat === 'IMAGE' && '📷 IMAGE HEADER'}
                        {headerFormat === 'VIDEO' && '🎥 VIDEO HEADER'}
                        {headerFormat === 'DOCUMENT' && '📄 DOCUMENT HEADER'}
                        {' '}· Media ID
                        {hasMediaId ? ' ✓' : ' ⚠️ Required'}
                      </div>

                      {!isEditing ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <code style={{
                            fontSize: 11, flex: 1,
                            color: hasMediaId ? '#15803d' : '#999',
                            fontStyle: hasMediaId ? 'normal' : 'italic',
                          }}>
                            {hasMediaId ? t.headerMediaId : 'Not set — required for broadcast'}
                          </code>
                          <button
                            onClick={() => { setEditingMediaId(t.id); setMediaIdInput(t.headerMediaId || ''); }}
                            style={{
                              fontSize: 11, padding: '2px 10px', borderRadius: 6,
                              border: '1px solid #128C7E', background: '#fff',
                              color: '#128C7E', cursor: 'pointer', whiteSpace: 'nowrap',
                            }}
                          >
                            {hasMediaId ? 'Edit' : 'Set ID'}
                          </button>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', gap: 6 }}>
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            placeholder="Paste Meta media ID"
                            value={mediaIdInput}
                            onChange={e => setMediaIdInput(e.target.value)}
                            autoFocus
                          />
                          <button
                            className="btn btn-sm btn-success"
                            onClick={() => saveMediaId(t.id)}
                            disabled={savingMediaId}
                            style={{ whiteSpace: 'nowrap' }}
                          >
                            {savingMediaId ? '...' : 'Save'}
                          </button>
                          <button
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => { setEditingMediaId(null); setMediaIdInput(''); }}
                          >
                            ✕
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Actions */}
                  <div style={{ padding: '10px 16px', display: 'flex', justifyContent: 'flex-end' }}>
                    <button
                      className="btn btn-sm btn-success"
                      onClick={() => openSend(t)}
                      disabled={t.status !== 'APPROVED'}
                      style={{ fontSize: 12 }}
                    >
                      <i className="fas fa-paper-plane mr-1"></i>
                      {t.status === 'APPROVED' ? 'Send' : 'Not Approved'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Send Modal */}
        {sendModal && selectedTemplate && (
          <div style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
            zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <div style={{
              background: '#fff', borderRadius: 12, width: 500, maxHeight: '85vh',
              overflow: 'auto', padding: 28, boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
            }}>
              <h5 style={{ marginBottom: 6, fontWeight: 600 }}>Send Template</h5>
              <p style={{ fontSize: 12, color: '#999', marginBottom: 20, fontFamily: 'monospace' }}>
                {selectedTemplate.name}
              </p>

              {/* Preview */}
              <div style={{
                background: '#f0faf8', border: '1px solid #b2dfdb',
                borderRadius: 8, padding: 14, marginBottom: 20,
              }}>
                <div style={{ fontSize: 11, color: '#128C7E', marginBottom: 6, fontWeight: 600 }}>PREVIEW</div>
                <div style={{ fontSize: 13, lineHeight: 1.6, color: '#333' }}>
                  {getBodyText(selectedTemplate)}
                </div>
              </div>

              {/* Recipient */}
              <div className="form-group">
                <label style={{ fontWeight: 500, fontSize: 13 }}>Send To</label>
                <select
                  className="form-control"
                  value={sendTo}
                  onChange={e => setSendTo(e.target.value)}
                >
                  <option value="">— Select contact —</option>
                  {contacts.map(c => (
                    <option key={c.id} value={c.phone}>
                      {c.name || c.phone} ({c.phone})
                    </option>
                  ))}
                </select>
                <small style={{ color: '#999', fontSize: 11 }}>
                  Or type phone number directly:
                </small>
                <input
                  type="text"
                  className="form-control mt-1"
                  placeholder="919876543210 (with country code)"
                  value={sendTo}
                  onChange={e => setSendTo(e.target.value)}
                />
              </div>

              {/* Parameters */}
              {params.length > 0 && (
                <div className="form-group">
                  <label style={{ fontWeight: 500, fontSize: 13 }}>Parameters</label>
                  {params.map((p, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                      <span style={{ fontFamily: 'monospace', fontSize: 12, color: '#999', width: 36 }}>
                        {`{{${i + 1}}}`}
                      </span>
                      <input
                        type="text"
                        className="form-control"
                        placeholder={`Value for {{${i + 1}}}`}
                        value={p}
                        onChange={e => {
                          const newParams = [...params];
                          newParams[i] = e.target.value;
                          setParams(newParams);
                        }}
                      />
                    </div>
                  ))}
                </div>
              )}

              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
                <button className="btn btn-outline-secondary" onClick={() => setSendModal(false)}>Cancel</button>
                <button className="btn btn-success" onClick={sendTemplate} disabled={sending || !sendTo}>
                  {sending ? 'Sending...' : 'Send Template'}
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}

export default Templates;